<template>
	<!-- the global Aktivitätsprotokoll's filter bar; the host passes the state on to
	     OperatorAuditList, which reloads from the first page on every change -->
	<div class="audit-filters" data-cy="auditFilters">
		<!-- matches the admin's e-mail and the operator's e-mail or name, deleted operators included -->
		<v-text-field variant="outlined" density="compact" hide-details clearable data-cy="auditFilterSearch"
			class="filter search"
			prepend-inner-icon="mdi-magnify"
			:label="label('operatorAuditFilterSearch', 'Search by e-mail or name')"
			:model-value="searchText"
			@update:modelValue="onSearchInput"
		/>
		<v-autocomplete variant="outlined" density="compact" hide-details clearable data-cy="auditFilterOperator"
			class="filter wide"
			:label="label('operatorAuditFilterOperator', 'Operator')"
			:items="operators"
			:item-title="operator => operator.name ? `${ operator.name } (${ operator.email })` : operator.email"
			item-value="id"
			:model-value="modelValue.userId ?? null"
			@update:modelValue="set('userId', $event)"
		/>
		<v-autocomplete variant="outlined" density="compact" hide-details clearable data-cy="auditFilterAdmin"
			class="filter wide"
			:label="label('operatorAuditFilterAdmin', 'Changed by')"
			:items="actors"
			:model-value="modelValue.actorEmail ?? null"
			@update:modelValue="set('actorEmail', $event)"
		/>
		<v-text-field variant="outlined" density="compact" hide-details clearable type="date" data-cy="auditFilterFrom"
			class="filter date"
			:label="label('operatorAuditFilterFrom', 'From')"
			:max="modelValue.to || undefined"
			:model-value="modelValue.from ?? ''"
			@update:modelValue="set('from', $event)"
		/>
		<v-text-field variant="outlined" density="compact" hide-details clearable type="date" data-cy="auditFilterTo"
			class="filter date"
			:label="label('operatorAuditFilterTo', 'To')"
			:min="modelValue.from || undefined"
			:model-value="modelValue.to ?? ''"
			@update:modelValue="set('to', $event)"
		/>
		<v-select variant="outlined" density="compact" hide-details clearable multiple chips closable-chips data-cy="auditFilterEvents"
			class="filter events"
			:label="label('operatorAuditFilterEvents', 'Event')"
			:items="eventItems"
			:item-props="item => ({ 'data-cy': `option-${ item.value }` })"
			:model-value="modelValue.events ?? []"
			@update:modelValue="set('events', $event)"
		/>
		<a v-if="active" class="reset" data-cy="auditFilterReset" @click="reset">
			{{ label('operatorAuditFilterReset', 'Reset filters') }}
		</a>
	</div>
</template>

<script>
import debounce from 'lodash/debounce'
import { EVENT_FALLBACKS, translatedLabel } from './operator-audit-lines'
import { hasAuditFilters } from './operator-audit-filters'

// typing pauses this long before the log reloads
const SEARCH_DEBOUNCE_MS = 400

export default {
	name: 'OperatorAuditFilters',
	props: {
		// { userId?, actorEmail?, from?: 'YYYY-MM-DD', to?: 'YYYY-MM-DD', events?: string[], search?: string }
		modelValue: { type: Object, default: () => ({}) },
		// the client's current operators (picker options)
		operators: { type: Array, default: () => [] },
		// every acting e-mail in the client's log (AuditFeedResult.actors)
		actors: { type: Array, default: () => [] },
	},
	emits: [ 'update:modelValue' ],
	data() {
		// what is typed; it reaches modelValue.search once typing pauses
		return { searchText: this.modelValue.search ?? '' }
	},
	watch: {
		// the host changed it (e.g. reset): show that
		'modelValue.search'(search) {
			if ((search ?? '') !== this.searchText.trim()) this.searchText = search ?? ''
		},
	},
	created() {
		this.emitSearch = debounce(() => this.set('search', this.searchText.trim()), SEARCH_DEBOUNCE_MS)
	},
	beforeUnmount() {
		this.emitSearch.cancel()
	},
	computed: {
		eventItems() {
			return Object.entries(EVENT_FALLBACKS).map(([ event, fallback ]) => ({ value: event, title: this.label(`operatorAuditEvent_${ event }`, fallback) }))
		},
		active() {
			return hasAuditFilters(this.modelValue)
		},
	},
	methods: {
		// a new object on every change, so the list's watcher sees it
		set(key, value) {
			this.$emit('update:modelValue', { ...this.modelValue, [key]: value || (key === 'events' ? [] : null) })
		},
		onSearchInput(value) {
			this.searchText = value ?? ''
			this.emitSearch()
			// clearing the field applies at once
			if (!this.searchText) this.emitSearch.flush()
		},
		reset() {
			this.emitSearch.cancel()
			this.searchText = ''
			this.$emit('update:modelValue', {})
		},
		label(key, fallback) {
			return translatedLabel(this, key, fallback)
		},
	},
}
</script>

<style scoped>
.audit-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 10px 0; }
.filter.search { flex: 1 1 100%; }
.filter.wide { flex: 1 1 220px; }
.filter.date { flex: 0 1 170px; }
.filter.events { flex: 1 1 100%; }
.reset { font-size: 12px; cursor: pointer; white-space: nowrap; }
</style>
